import React, { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ConfirmModal } from "global/components/AppModal";
import { capitalizeText } from "helpers/methods";
import { CustomField } from "models/interfaces";
import CustomFieldModal from "../../components/CustomFieldModal";
import useCustomFieldLibrary from "../../components/useCustomFieldLibrary";

/**
 * "Custom fields" sheet of the mobile editor: the sender's custom field
 * library with Create / Edit / Delete, the mobile counterpart of the desktop
 * Custom Fields section in FieldsPanel. Placing a field stays on the palette
 * chips (they are drag handles, so the actions live here instead).
 */
const MobileCustomFieldsSheet = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const {
    customFields,
    save: saveCustomField,
    remove: removeCustomField,
    canManage,
  } = useCustomFieldLibrary();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CustomField | null>(null);
  const [deleting, setDeleting] = useState<CustomField | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (field: CustomField) => {
    setEditing(field);
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting?.id) return;
    setDeletingBusy(true);
    const removed = await removeCustomField(deleting.id);
    setDeletingBusy(false);
    if (removed) setDeleting(null);
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { px: 2, pb: 3, borderTopLeftRadius: 0, borderTopRightRadius: 0 },
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": "custom-fields-sheet-title",
        }}
      >
        {/* Drag handle */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1.5 }}>
          <Box
            aria-hidden="true"
            sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
          />
        </Box>

        <Typography
          id="custom-fields-sheet-title"
          component="h2"
          sx={{ fontSize: "1rem", fontWeight: 600, color: m.base[900], mb: 0.5 }}
        >
          Custom Fields
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: m.grey[400], mb: 2 }}>
          Create a field once and drag it onto any document from the field bar.
        </Typography>

        <Box sx={{ maxHeight: "50vh", overflowY: "auto", mb: 2.5 }}>
          {customFields.length === 0 ? (
            <Typography
              sx={{ fontSize: "0.875rem", color: m.grey[400], py: 2 }}
            >
              No custom fields yet.
            </Typography>
          ) : (
            <Stack component="ul" spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
              {customFields.map((field) => (
                <Box
                  component="li"
                  key={field.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    border: `1px solid ${m.grey[50]}`,
                    borderRadius: "12px",
                    pl: 1.5,
                    pr: 0.5,
                    py: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: m.base[900],
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {field.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: m.grey[400] }}>
                      {capitalizeText(field.fieldType.replace(/_/g, " "))}
                    </Typography>
                  </Box>
                  {canManage && (
                    <>
                  <IconButton
                    aria-label={`Edit ${field.name}`}
                    onClick={() => openEdit(field)}
                    sx={{ color: m.grey[400], width: 44, height: 44 }}
                  >
                    <EditOutlined fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label={`Delete ${field.name}`}
                    onClick={() => setDeleting(field)}
                    sx={{ color: theme.palette.error.main, width: 44, height: 44 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                    </>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {canManage && (
        <Button
          fullWidth
          variant="contained"
          onClick={openCreate}
          sx={{
            bgcolor: m.ePurple[500],
            color: m.surface.white,
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 700,
            lineHeight: "20px",
            borderRadius: "8px",
            py: 2,
            boxShadow: "none",
            "&:hover": { bgcolor: m.ePurple[700], boxShadow: "none" },
          }}
        >
          Create custom field
        </Button>
        )}
      </Drawer>

      <CustomFieldModal
        open={modalOpen}
        customField={editing}
        existingNames={customFields.map((field) => field.name)}
        onClose={() => setModalOpen(false)}
        onSave={saveCustomField}
      />

      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete custom field?"
        message={`"${deleting?.name ?? ""}" will be removed from your custom fields. Fields already placed on documents are not affected.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        loading={deletingBusy}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default MobileCustomFieldsSheet;
